import React, { useState, useEffect } from "react";
import { Modal, Form, Row, Col, Spinner } from "react-bootstrap";
import { SubmitButton, CancelButton } from "../shared";

const BONUS_PERIODS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
];

function formatDate(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getRangeForPeriod(period) {
  const today = new Date();

  if (period === "monthly") {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { startDate: formatDate(start), endDate: formatDate(end) };
  } else if (period === "quarterly") {
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const prevQuarterStart = ((currentQuarter - 1 + 4) % 4) * 3;
    const year =
      currentQuarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const start = new Date(year, prevQuarterStart, 1);
    const end = new Date(year, prevQuarterStart + 3, 0);
    return { startDate: formatDate(start), endDate: formatDate(end) };
  } else {
    const prevYear = today.getFullYear() - 1;
    const start = new Date(prevYear, 0, 1);
    const end = new Date(prevYear, 11, 31);
    return { startDate: formatDate(start), endDate: formatDate(end) };
  }
}

function getDateParts(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!match) return null;

  const parts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));

  return date.getUTCFullYear() === parts.year &&
    date.getUTCMonth() === parts.month - 1 &&
    date.getUTCDate() === parts.day
    ? parts
    : null;
}

function getPeriodEndDate(start, months) {
  const targetMonth = start.month - 1 + months;
  const targetYear = start.year + Math.floor(targetMonth / 12);
  const targetMonthIndex = targetMonth % 12;
  const lastDayOfTargetMonth = new Date(
    targetYear,
    targetMonthIndex + 1,
    0,
  ).getDate();
  const targetDay = Math.min(start.day, lastDayOfTargetMonth);
  return new Date(targetYear, targetMonthIndex, targetDay);
}

function getCalendarPeriodEndDate(start, months) {
  const targetMonth = start.month - 1 + months - 1;
  const targetYear = start.year + Math.floor(targetMonth / 12);
  const targetMonthIndex = targetMonth % 12;
  return new Date(targetYear, targetMonthIndex + 1, 0);
}

function getPeriodValidationMessage(period, startDate, endDate) {
  const start = getDateParts(startDate);
  const end = getDateParts(endDate);
  if (!start || !end) return "Enter both a start date and an end date.";

  const months = period === "monthly" ? 1 : period === "quarterly" ? 3 : 12;
  const anniversaryEndDate = formatDate(getPeriodEndDate(start, months));
  const calendarEndDate = formatDate(getCalendarPeriodEndDate(start, months));

  if (
    endDate !== anniversaryEndDate &&
    !(start.day === 1 && endDate === calendarEndDate)
  ) {
    const periodLabel = period === "annual" ? "year" : period.replace("ly", "");
    return `The selected ${periodLabel} period must be exactly ${months === 1 ? "one month" : months === 3 ? "three months" : "one year"} long.`;
  }

  return "";
}

export function CalculateBonusModal({
  show,
  onHide,
  customers,
  loading,
  submitting,
  onSubmit,
  initialCustomerCode,
}) {
  const [customerCode, setCustomerCode] = useState("");
  const [period, setPeriod] = useState("monthly");
  const [startDate, setStartDate] = useState(
    () => getRangeForPeriod("monthly").startDate,
  );
  const [endDate, setEndDate] = useState(
    () => getRangeForPeriod("monthly").endDate,
  );

  useEffect(() => {
    if (!show) return;
    const initialPeriod = "monthly";
    setPeriod(initialPeriod);
    const range = getRangeForPeriod(initialPeriod);
    setStartDate(range.startDate);
    setEndDate(range.endDate);

    if (customers.length === 1) {
      setCustomerCode(customers[0].code);
      return;
    }

    if (initialCustomerCode) {
      setCustomerCode(initialCustomerCode);
      return;
    }

    setCustomerCode("");
  }, [show, customers, initialCustomerCode]);

  const handlePeriodChange = (e) => {
    const newPeriod = e.target.value;
    setPeriod(newPeriod);
    const range = getRangeForPeriod(newPeriod);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const periodValidationMessage = getPeriodValidationMessage(
    period,
    startDate,
    endDate,
  );

  const canSubmit =
    !loading &&
    !submitting &&
    customerCode &&
    period &&
    startDate &&
    endDate &&
    !periodValidationMessage;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    await onSubmit({
      customerCode,
      startDate,
      endDate,
    });
  };

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      centered
      style={{ overflow: "hidden" }}
      dialogClassName="calculate-bonus-modal"
      className="commission-modal-scope"
      backdrop="static"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Calculate Bonus</Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{ overflow: "visible", maxHeight: "70vh", overflowY: "auto" }}
        >
          {loading ? (
            <div className="d-flex align-items-center justify-content-center py-4">
              <Spinner
                animation="border"
                role="status"
                size="sm"
                className="me-2"
              />
              <span>Loading customer profile...</span>
            </div>
          ) : (
            <>
              <Row className="g-3">
                {customers && customers.length > 1 && (
                  <Col xs={12}>
                    <Form.Group controlId="bonusCustomerCode">
                      <Form.Label>Customer</Form.Label>
                      <Form.Select
                        value={customerCode}
                        onChange={(e) => setCustomerCode(e.target.value)}
                        disabled={
                          submitting ||
                          customers.length === 0 ||
                          customers.length === 1
                        }
                      >
                        <option value="">Select customer</option>
                        {customers.map((customer) => (
                          <option key={customer.code} value={customer.code}>
                            {customer.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
                <Col xs={12}>
                  <Form.Group controlId="bonusPeriod">
                    <Form.Label>Period</Form.Label>
                    <Form.Select
                      value={period}
                      onChange={handlePeriodChange}
                      disabled={submitting}
                    >
                      {BONUS_PERIODS.map((bonusPeriod) => (
                        <option
                          key={bonusPeriod.value}
                          value={bonusPeriod.value}
                        >
                          {bonusPeriod.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group controlId="bonusStartDate">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      disabled={submitting}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group controlId="bonusEndDate">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={submitting}
                      isInvalid={Boolean(periodValidationMessage)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {periodValidationMessage}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <CancelButton onClick={onHide} disabled={submitting} />
          <SubmitButton
            type="submit"
            text="Submit"
            color="blue"
            disabled={!canSubmit}
            isSubmitting={submitting}
          />
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
